/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\InboundRouteSettingsSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Calls Routing module.
----------------------------------------------------------------*/


import { Settings, Network } from 'lucide-react';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import { FormBox } from '../../../components/ui/FormBox';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import RecordingSelect from '../../../components/ui/RecordingSelect';
import { CallerIdInput } from '../../../components/ui/CallerIdInput';
import { MultiSelectModal } from '../../../components/ui/MultiSelectModal';
import { InboundRouteSettings } from '../types';

interface InboundRouteSettingsSectionProps {
  settings: InboundRouteSettings;
  errors: Record<string, string>;
  availableGateways: Array<{ id: string; name: string; description: string }>;
  showAllowedGatewaysModal: boolean;
  setShowAllowedGatewaysModal: (open: boolean) => void;
  onSettingChange: <K extends keyof InboundRouteSettings>(key: K, value: InboundRouteSettings[K]) => void;
  t: (key: string, fallback?: string) => string;
}

export function InboundRouteSettingsSection({
  settings, errors, availableGateways,
  showAllowedGatewaysModal, setShowAllowedGatewaysModal,
  onSettingChange, t,
}: InboundRouteSettingsSectionProps) {
  return (
    <>
      <FormBox
        title={t('inboundRoutes:sections.settings')}
        icon={<Settings className="h-5 w-5" />}
        className="mt-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
          {/* Record Call */}
          <Label className="pt-2">
            <SimpleTooltip content={t('inboundRoutes:fields.recordCall.tooltip')}>
              {t('inboundRoutes:fields.recordCall.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Toggle value={settings.recordCall} onChange={(v) => onSettingChange('recordCall', v)} />
          </div>

          {/* Block Anonymous */}
          <Label className="pt-2">
            <SimpleTooltip content={t('inboundRoutes:fields.blockAnonymous.tooltip')}>
              {t('inboundRoutes:fields.blockAnonymous.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Toggle value={settings.blockAnonymous} onChange={(v) => onSettingChange('blockAnonymous', v)} />
          </div>

          {/* Log Calls */}
          <Label className="pt-2">
            <SimpleTooltip content={t('inboundRoutes:fields.logCalls.tooltip')}>
              {t('inboundRoutes:fields.logCalls.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Toggle value={settings.logCalls} onChange={(v) => onSettingChange('logCalls', v)} />
          </div>

          {/* Number Manipulation — Strip */}
          <Label className="pt-2" htmlFor="numberManipulationStrip">
            <SimpleTooltip content={t('inboundRoutes:fields.numberManipulationStrip.tooltip')}>
              {t('inboundRoutes:fields.numberManipulationStrip.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Input
              id="numberManipulationStrip"
              type="number"
              min={0}
              value={settings.numberManipulationStrip}
              onChange={(e) => onSettingChange('numberManipulationStrip', parseInt(e.target.value, 10) || 0)}
            />
          </div>

          {/* Number Manipulation — Add */}
          <Label className="pt-2" htmlFor="numberManipulationAdd">
            <SimpleTooltip content={t('inboundRoutes:fields.numberManipulationAdd.tooltip')}>
              {t('inboundRoutes:fields.numberManipulationAdd.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Input
              id="numberManipulationAdd"
              value={settings.numberManipulationAdd}
              onChange={(e) => onSettingChange('numberManipulationAdd', e.target.value)}
              placeholder={t('inboundRoutes:fields.numberManipulationAdd.placeholder')}
            />
          </div>

          {/* Announce Path */}
          <Label className="pt-2" htmlFor="announcePath">
            <SimpleTooltip content={t('inboundRoutes:fields.announcePath.tooltip')}>
              {t('inboundRoutes:fields.announcePath.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <RecordingSelect
              value={settings.announcePath}
              onChange={(v) => onSettingChange('announcePath', v || '')}
              placeholder={t('inboundRoutes:fields.announcePath.placeholder')}
              returnType="id"
              showPlayButton
            />
          </div>

          {/* CallerID Rewrite */}
          <Label className="pt-2">
            <SimpleTooltip content={t('inboundRoutes:fields.callerIdNameRewrite.tooltip')}>
              {t('inboundRoutes:fields.callerIdNameRewrite.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <CallerIdInput
              id="callerIdRewrite"
              nameValue={settings.callerIdNameRewrite}
              numberValue={settings.callerIdNumberRewrite}
              onNameChange={(v) => onSettingChange('callerIdNameRewrite', v)}
              onNumberChange={(v) => onSettingChange('callerIdNumberRewrite', v)}
              namePlaceholder={t('inboundRoutes:fields.callerIdNameRewrite.placeholder')}
              numberPlaceholder={t('inboundRoutes:fields.callerIdNumberRewrite.placeholder')}
              nameTooltip={t('inboundRoutes:fields.callerIdNameRewrite.tooltip')}
              numberTooltip={t('inboundRoutes:fields.callerIdNumberRewrite.tooltip')}
            />
          </div>

          {/* Allowed Gateways chip picker */}
          <Label className="pt-2 self-start">
            <SimpleTooltip content={t('inboundRoutes:fields.allowedGateways.tooltip')}>
              {t('inboundRoutes:fields.allowedGateways.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setShowAllowedGatewaysModal(true)}
              onKeyDown={(e) => e.key === 'Enter' && setShowAllowedGatewaysModal(true)}
              className="min-h-[2.5rem] w-full px-3 py-2 border border-border rounded-md bg-background flex flex-wrap gap-1.5 items-center cursor-pointer hover:bg-muted/30 transition-colors"
            >
              {settings.allowedGateways.length === 0 ? (
                <>
                  <Network className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm text-muted-foreground flex-1">
                    {t('inboundRoutes:fields.allowedGateways.placeholderUnrestricted', 'Any gateway (Unrestricted)')}
                  </span>
                  <span className="text-xs text-primary hover:underline ml-auto">
                    + {t('inboundRoutes:fields.allowedGateways.restrict', 'Restrict')}
                  </span>
                </>
              ) : (
                <>
                  {settings.allowedGateways.map((gwId) => {
                    const gw = availableGateways.find((g) => g.id === gwId);
                    return gw ? (
                      <span key={gwId} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-primary/10 text-primary text-xs rounded-md font-medium border border-primary/20">
                        <Network className="h-3 w-3" />
                        {gw.name}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSettingChange('allowedGateways', settings.allowedGateways.filter((id) => id !== gwId));
                          }}
                          className="ml-0.5 text-primary/60 hover:text-destructive transition-colors text-base leading-none"
                          aria-label={`Remove ${gw.name}`}
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setShowAllowedGatewaysModal(true); }}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors px-1 border border-dashed border-border rounded"
                  >
                    + {t('common.add', 'Add')}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Max Concurrent */}
          <Label className="pt-2" htmlFor="maxConcurrent">
            <SimpleTooltip content={t('inboundRoutes:fields.maxConcurrent.tooltip')}>
              {t('inboundRoutes:fields.maxConcurrent.label')}
            </SimpleTooltip>
          </Label>
          <div>
            <Input
              id="maxConcurrent"
              type="number"
              min={1}
              max={100}
              value={settings.maxConcurrent ?? ''}
              onChange={(e) => onSettingChange('maxConcurrent', e.target.value ? parseInt(e.target.value, 10) : null)}
              placeholder={t('inboundRoutes:fields.maxConcurrent.placeholder')}
              className={errors.maxConcurrent ? 'border-red-500' : ''}
            />
            {errors.maxConcurrent && <p className="text-sm text-red-500 mt-1">{errors.maxConcurrent}</p>}
          </div>
        </div>
      </FormBox>

      {/* Allowed Gateways Multi-Select Modal */}
      <MultiSelectModal
        isOpen={showAllowedGatewaysModal}
        onClose={() => setShowAllowedGatewaysModal(false)}
        title={t('inboundRoutes:fields.allowedGateways.modalTitle')}
        description={t('inboundRoutes:fields.allowedGateways.modalDescription')}
        items={availableGateways}
        selectedIds={settings.allowedGateways}
        onSelectionChange={(selectedIds) => onSettingChange('allowedGateways', selectedIds)}
        searchPlaceholder={t('inboundRoutes:fields.allowedGateways.searchPlaceholder')}
        icon={Network}
      />
    </>
  );
}
